import { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import {
    BookOpen,
    Download,
    Loader2,
    Search,
    Euro,
    Columns2,
    Columns3,
    LayoutGrid,
    Plus,
    X,
    Tag,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { downloadRestaurantMenuPdf } from "../lib/restaurantMenuPdf";
import BrandWizardDialog from "./BrandWizardDialog";

const CATEGORY_ORDER = [
    "Antipasti",
    "Primi",
    "Secondi",
    "Pizze",
    "Contorni",
    "Dolci",
    "Bevande",
];

const ALLERGENS_PRESET = [
    "Glutine", "Lattosio", "Uova", "Pesce", "Crostacei", "Molluschi",
    "Frutta a guscio", "Soia", "Arachidi", "Sedano", "Senape", "Sesamo",
    "Solfiti", "Lupini",
];

/**
 * Genera il "Tuo Menù Ristorante" partendo dai piatti già nel database utente.
 * - Carica /api/recipes
 * - Raggruppa per categoria
 * - Per ogni piatto: checkbox include + nome + descrizione + prezzo + allergeni
 * - Costo coperto (opzionale)
 * - Layout: 1 o 2 colonne
 * - Generate → BrandWizardDialog → PDF brandizzato
 */
export default function RestaurantMenuBuilderDialog({ open, onOpenChange }) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [title, setTitle] = useState("Menu");
    const [restaurantName, setRestaurantName] = useState("");
    const [message, setMessage] = useState("");
    const [coperto, setCoperto] = useState(0);
    const [columns, setColumns] = useState(2);
    const [search, setSearch] = useState("");
    const [items, setItems] = useState([]); // { id, name, category, included, description, price_eur, allergens }
    const [brandOpen, setBrandOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        setRestaurantName(user?.restaurant_name || "");
        setTitle("Menu");
        setMessage("");
        setCoperto(0);
        setColumns(2);
        setSearch("");
        setLoading(true);
        api.get("/recipes")
            .then(({ data }) => {
                const seed = (data || [])
                    .filter((r) => CATEGORY_ORDER.includes(r.category))
                    .map((r) => ({
                        id: r.id,
                        name: r.name || "",
                        category: r.category,
                        included: true,
                        description: r.notes || "",
                        price_eur:
                            r.selling_price && r.selling_price > 0
                                ? Number(r.selling_price)
                                : 0,
                        allergens: Array.isArray(r.allergens) ? [...r.allergens] : [],
                    }));
                setItems(seed);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [open, user]);

    const grouped = useMemo(() => {
        const filtered = search
            ? items.filter((it) =>
                  it.name.toLowerCase().includes(search.toLowerCase()),
              )
            : items;
        const map = new Map();
        CATEGORY_ORDER.forEach((c) => map.set(c, []));
        filtered.forEach((it) => {
            if (!map.has(it.category)) map.set(it.category, []);
            map.get(it.category).push(it);
        });
        return Array.from(map.entries())
            .filter(([, arr]) => arr.length > 0)
            .map(([category, dishes]) => ({ category, dishes }));
    }, [items, search]);

    const totalSelected = items.filter((it) => it.included && it.name.trim()).length;

    const updateItem = (id, patch) =>
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

    const removeItem = (id) =>
        setItems((prev) => prev.filter((it) => it.id !== id));

    const addCustomDish = (category) => {
        const newItem = {
            id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            name: "",
            category,
            included: true,
            description: "",
            price_eur: 0,
            allergens: [],
        };
        setItems((prev) => [...prev, newItem]);
    };

    const toggleCategory = (cat, on) => {
        setItems((prev) =>
            prev.map((it) =>
                it.category === cat ? { ...it, included: on } : it,
            ),
        );
    };

    const handleConfirm = () => {
        const selected = items.filter((it) => it.included && it.name.trim());
        if (selected.length === 0) {
            toast.error("Seleziona almeno un piatto da includere nel menù");
            return;
        }
        // raggruppa per categoria mantenendo l'ordine CATEGORY_ORDER
        const sections = [];
        CATEGORY_ORDER.forEach((c) => {
            const dishes = selected
                .filter((s) => s.category === c)
                .map((s) => ({
                    name: s.name.trim(),
                    description: (s.description || "").trim(),
                    allergens: s.allergens || [],
                    price_eur: Number(s.price_eur) || null,
                }));
            if (dishes.length) sections.push({ category: c, dishes });
        });
        setBrandOpen(true);
        // Salva sul backend (best-effort, non blocca il PDF)
        const payload = {
            title: title.trim() || "Menu",
            restaurant_name: restaurantName.trim(),
            message: message.trim(),
            coperto: Number(coperto) || 0,
            columns,
            sections,
        };
        api.post("/restaurant-menus", payload).catch(() => {});
        // Pre-store the payload to use on brand-confirm
        setPendingMenu(payload);
    };

    const [pendingMenu, setPendingMenu] = useState(null);

    const onBrandConfirm = (brand) => {
        if (!pendingMenu) return;
        downloadRestaurantMenuPdf(pendingMenu, brand);
        onOpenChange(false);
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    className="max-w-4xl bg-white p-0 overflow-hidden max-h-[92vh] flex flex-col"
                    data-testid="restaurant-menu-dialog"
                >
                    <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-brand-gold/15 to-paper">
                        <div className="flex items-start gap-3">
                            <div className="h-11 w-11 rounded-full bg-brand-navy text-brand-gold flex items-center justify-center shrink-0">
                                <BookOpen size={20} />
                            </div>
                            <div className="min-w-0">
                                <DialogTitle className="font-display text-2xl text-brand-ink leading-tight">
                                    Crea il tuo Menù Ristorante
                                </DialogTitle>
                                <DialogDescription className="text-sm text-brand-muted mt-1">
                                    Seleziona i piatti dal tuo database, aggiungi il
                                    costo del coperto e genera un PDF pronto da stampare.
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto p-6 space-y-5">
                        {/* Top form */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Titolo del menù">
                                <input
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Es. Menu, Carta, Menù della Casa"
                                    data-testid="rmenu-title"
                                    className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                />
                            </Field>
                            <Field label="Nome ristorante">
                                <input
                                    value={restaurantName}
                                    onChange={(e) => setRestaurantName(e.target.value)}
                                    placeholder="Compare nell'intestazione"
                                    data-testid="rmenu-restaurant-name"
                                    className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                />
                            </Field>
                        </div>

                        <Field label="Messaggio per i clienti (opzionale)">
                            <textarea
                                rows={2}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Es. Tutti i nostri piatti sono preparati al momento · Pane in omaggio"
                                data-testid="rmenu-message"
                                className="w-full px-3 py-2 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold resize-none"
                            />
                        </Field>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Costo coperto (€ — 0 = non mostrato)">
                                <div className="relative">
                                    <Euro
                                        size={13}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
                                    />
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        value={coperto}
                                        onChange={(e) => setCoperto(e.target.value)}
                                        data-testid="rmenu-coperto"
                                        className="w-full pl-8 pr-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                    />
                                </div>
                            </Field>
                            <Field label="Layout pagina">
                                <div className="inline-flex items-center gap-1.5 bg-brand-cream/60 rounded-lg p-1 border border-brand-line w-fit">
                                    <ColBtn
                                        active={columns === 1}
                                        onClick={() => setColumns(1)}
                                        icon={<LayoutGrid size={13} />}
                                        label="1 colonna"
                                        testid="rmenu-cols-1"
                                    />
                                    <ColBtn
                                        active={columns === 2}
                                        onClick={() => setColumns(2)}
                                        icon={<Columns2 size={13} />}
                                        label="2 colonne"
                                        testid="rmenu-cols-2"
                                    />
                                </div>
                            </Field>
                        </div>

                        {/* Search */}
                        <div className="relative">
                            <Search
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
                            />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Cerca un piatto…"
                                data-testid="rmenu-search"
                                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                            />
                        </div>

                        {loading ? (
                            <div className="py-10 text-center text-brand-muted text-sm">
                                <Loader2 size={20} className="mx-auto animate-spin mb-2" />
                                Carico i piatti…
                            </div>
                        ) : items.length === 0 ? (
                            <div className="py-10 text-center text-brand-muted text-sm">
                                Nessun piatto disponibile. Aggiungi piatti in
                                <strong className="text-brand-ink"> /piatti</strong> e
                                riprova.
                            </div>
                        ) : (
                            grouped.map(({ category, dishes }) => (
                                <CategoryBlock
                                    key={category}
                                    category={category}
                                    dishes={dishes}
                                    onUpdate={updateItem}
                                    onRemove={removeItem}
                                    onAdd={() => addCustomDish(category)}
                                    onToggleAll={(on) => toggleCategory(category, on)}
                                />
                            ))
                        )}
                    </div>

                    <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-[11px] text-brand-muted">
                            <strong className="text-brand-ink">{totalSelected}</strong>{" "}
                            piatti selezionati · Layout {columns} colonna
                            {columns > 1 ? "e" : ""}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={totalSelected === 0}
                                data-testid="rmenu-generate"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition disabled:opacity-50 shadow"
                            >
                                <Download size={14} /> Personalizza & Scarica
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <BrandWizardDialog
                open={brandOpen}
                onOpenChange={setBrandOpen}
                onConfirm={onBrandConfirm}
                title="Stile del PDF Menù Ristorante"
                confirmLabel="Scarica Menù"
            />
        </>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="eyebrow text-brand-ink/70 mb-1.5">{label}</div>
            {children}
        </div>
    );
}

function ColBtn({ active, onClick, icon, label, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold transition ${
                active
                    ? "bg-brand-navy text-white shadow"
                    : "text-brand-ink hover:bg-white"
            }`}
        >
            {icon}
            {label}
        </button>
    );
}

function CategoryBlock({ category, dishes, onUpdate, onRemove, onAdd, onToggleAll }) {
    const allOn = dishes.every((d) => d.included);
    const anyOn = dishes.some((d) => d.included);
    const count = dishes.filter((d) => d.included).length;
    return (
        <div className="rounded-xl border border-brand-line bg-paper/50 p-4">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <h3 className="font-display text-lg text-brand-ink">
                        {category}
                    </h3>
                    <span className="text-[11px] text-brand-muted">
                        ({count}/{dishes.length})
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onToggleAll(!allOn)}
                        className="text-[11px] font-semibold text-brand-navy hover:text-brand-gold transition"
                        data-testid={`rmenu-toggleall-${category}`}
                    >
                        {allOn ? "Deseleziona tutti" : anyOn ? "Seleziona tutti" : "Seleziona tutti"}
                    </button>
                    <button
                        type="button"
                        onClick={onAdd}
                        data-testid={`rmenu-addcustom-${category}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-navy hover:text-brand-gold transition"
                    >
                        <Plus size={11} /> Aggiungi
                    </button>
                </div>
            </div>
            <div className="space-y-2">
                {dishes.map((d) => (
                    <DishRow
                        key={d.id}
                        dish={d}
                        onUpdate={(patch) => onUpdate(d.id, patch)}
                        onRemove={() => onRemove(d.id)}
                    />
                ))}
            </div>
        </div>
    );
}

function DishRow({ dish, onUpdate, onRemove }) {
    const [expanded, setExpanded] = useState(false);
    const toggleAllergen = (a) => {
        const set = new Set(dish.allergens || []);
        if (set.has(a)) set.delete(a);
        else set.add(a);
        onUpdate({ allergens: Array.from(set) });
    };
    return (
        <div
            className={`rounded-lg border p-2.5 transition ${
                dish.included ? "border-brand-line bg-white" : "border-brand-line/50 bg-brand-cream/30 opacity-70"
            }`}
            data-testid={`rmenu-row-${dish.id}`}
        >
            <div className="flex items-start gap-2">
                <input
                    type="checkbox"
                    checked={dish.included}
                    onChange={(e) => onUpdate({ included: e.target.checked })}
                    className="mt-2 h-4 w-4 accent-brand-navy shrink-0"
                    data-testid={`rmenu-include-${dish.id}`}
                />
                <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2">
                        <input
                            value={dish.name}
                            onChange={(e) => onUpdate({ name: e.target.value })}
                            placeholder="Nome piatto"
                            data-testid={`rmenu-name-${dish.id}`}
                            className="flex-1 px-2.5 py-1.5 rounded-md bg-white border border-brand-line text-sm font-semibold focus:outline-none focus:border-brand-gold"
                        />
                        <div className="relative shrink-0">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] text-brand-muted">€</span>
                            <input
                                type="number"
                                step="0.5"
                                min="0"
                                value={dish.price_eur}
                                onChange={(e) => onUpdate({ price_eur: e.target.value })}
                                data-testid={`rmenu-price-${dish.id}`}
                                className="w-20 pl-6 pr-2 py-1.5 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            className="text-[10px] font-semibold text-brand-muted hover:text-brand-navy px-2 transition"
                            data-testid={`rmenu-expand-${dish.id}`}
                        >
                            {expanded ? "Meno" : "Più"}
                        </button>
                        <button
                            type="button"
                            onClick={onRemove}
                            className="h-7 w-7 rounded-full text-brand-muted hover:text-brand-danger hover:bg-red-50 flex items-center justify-center shrink-0 transition"
                            data-testid={`rmenu-remove-${dish.id}`}
                            title="Rimuovi dalla lista"
                        >
                            <X size={12} />
                        </button>
                    </div>
                    {expanded && (
                        <>
                            <textarea
                                rows={1}
                                value={dish.description}
                                onChange={(e) => onUpdate({ description: e.target.value })}
                                placeholder="Descrizione (opzionale)"
                                data-testid={`rmenu-desc-${dish.id}`}
                                className="w-full px-2.5 py-1.5 rounded-md bg-white border border-brand-line text-[12px] focus:outline-none focus:border-brand-gold resize-none"
                            />
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-brand-muted mb-1 inline-flex items-center gap-1">
                                    <Tag size={9} /> Allergeni
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {ALLERGENS_PRESET.map((a) => {
                                        const active = (dish.allergens || []).includes(a);
                                        return (
                                            <button
                                                key={a}
                                                type="button"
                                                onClick={() => toggleAllergen(a)}
                                                className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                                                    active
                                                        ? "bg-brand-navy text-white border-brand-navy font-bold"
                                                        : "bg-white text-brand-muted border-brand-line hover:border-brand-gold/60"
                                                }`}
                                            >
                                                {a}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
